<div class="min-h-screen bg-gray-50 flex flex-col justify-between font-sans relative">

  <div class="max-w-6xl mx-auto w-full p-4 flex flex-col flex-grow relative">
    <div [@chatExpand]="hasConversation ? 'expanded' : 'initial'"
      class="bg-white rounded-2xl shadow-xl flex flex-col h-full overflow-hidden relative">
      <!-- Top right icons container -->
      <div class="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 flex items-center gap-2">
        <app-vodafone-icon class="w-8 h-8 text-red-600"></app-vodafone-icon>
        <app-logout></app-logout>
      </div>

      <!-- Header -->
      <div class="px-4 sm:px-8 py-5 bg-gray-200 rounded-t-2xl">
        <h1 class="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{{ hasConversation ? 'Question Chat' : 'Ask a Question' }}</h1>
        <p class="text-sm sm:text-base mt-2 text-gray-900 flex items-center">
          Welcome,&nbsp;<span class="font-bold">{{ userName || 'User' }}</span>!&nbsp;<span class="ml-1">👋</span>
        </p>
        <p class="text-sm mt-1 text-gray-700">{{ hasConversation ? "We're here to help you find solutions" : "Type your
          question below to get help" }}</p>
      </div>

      <!-- Messages -->
      <div #messagesContainer *ngIf="hasConversation" [@fadeIn] class="px-4 sm:px-8 py-6 overflow-y-auto flex-1 space-y-3 sm:space-y-6 bg-gray-50">
        <!-- Message Bubbles -->
        <div *ngFor="let message of messages" class="flex"
          [ngClass]="message.type === 'user' ? 'justify-end' : 'justify-start'">
          <div class="max-w-3/4 p-4 rounded-lg"
            [ngClass]="message.type === 'user' ? 'bg-vodafone-red text-white rounded-br-none' : 'bg-gray-200 text-gray-800 rounded-bl-none'">

            <!-- User messages are displayed as plain text -->
            <p *ngIf="message.type === 'user'" class="text-sm whitespace-pre-wrap">{{ message.content }}</p>

            <!-- Assistant messages are displayed with markdown support -->
            <div *ngIf="message.type === 'assistant'" class="markdown-content text-sm">
              <div [innerHTML]="message.htmlContent"></div>
            </div>

            <p class="text-xs mt-1 opacity-70 text-right">
              {{ message.timestamp | date:'shortTime' }}
            </p>
          </div>
        </div>

        <!-- Loading Animation -->
        <div *ngIf="isSubmitting" class="flex items-center space-x-2">
          <div class="flex space-x-1">
            <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
            <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse animation-delay-200"></div>
            <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse animation-delay-400"></div>
          </div>
          <span class="text-gray-600 text-sm">Generating response...</span>
        </div>
      </div>

      <!-- Empty state filler when no conversation (just for layout) -->
      <div *ngIf="!hasConversation" class="flex-grow bg-gray-50"></div>

      <!-- Action Buttons -->
      <div *ngIf="hasConversation && currentAnswer" [@fadeIn] class="px-4 sm:px-8 py-4 bg-gray-100 border-t border-gray-300">
        <div class="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
          <button (click)="markHelpful()" [disabled]="feedbackGiven"
            class="text-sm text-green-600 hover:text-green-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            This helped me
          </button>

          <div class="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button (click)="markNotHelpful()" [disabled]="feedbackGiven"
              class="text-sm text-gray-600 hover:text-gray-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              This didn't help me
            </button>

            <button (click)="showTicketSidebar()" [disabled]="feedbackGiven"
              class="text-sm text-vodafone-red hover:text-red-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Open a ticket
            </button>
          </div>
        </div>
      </div>

      <!-- Chat Input -->
      <div class="px-4 sm:px-8 py-6 bg-gray-100 border-t border-gray-300" [style.min-height]="inputContainerHeight + 'px'">
        <app-question-form [isSubmitting]="isSubmitting" [feedbackGiven]="feedbackGiven"
          (heightChange)="onInputHeightChange($event)" (submitQuestion)="onQuestionSubmit($event)"></app-question-form>
      </div>
    </div>
  </div>

  <!-- Ticket Sidebar -->
  <div *ngIf="isTicketSidebarVisible" [@slideFade] class="fixed inset-0 overflow-hidden z-50"
    aria-labelledby="slide-over-title" role="dialog" aria-modal="true">
    <div class="absolute inset-0 overflow-hidden">
      <!-- Overlay -->
      <div class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"
        (click)="hideTicketSidebar()"></div>

      <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div class="w-screen max-w-md">
          <div class="h-full flex flex-col bg-white shadow-xl overflow-y-auto">
            <!-- Header -->
            <div class="px-4 py-6 bg-vodafone-red sm:px-6">
              <div class="flex items-center justify-between">
                <h2 id="slide-over-title" class="text-lg font-medium text-white">Create Ticket</h2>
                <div class="ml-3 h-7 flex items-center">
                  <button type="button"
                    class="bg-vodafone-red rounded-md text-white hover:text-gray-200 focus:outline-none"
                    (click)="hideTicketSidebar()">
                    <span class="sr-only">Close panel</span>
                    <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="mt-1">
                <p class="text-sm text-white">Fill in details for your support ticket.</p>
              </div>
            </div>

            <!-- Form -->
            <div class="flex-1 py-6 px-4 sm:px-6">
              <form [formGroup]="ticketForm" class="space-y-6">
                <div>
                  <label for="subject" class="block text-sm font-medium text-gray-700">Subject *</label>
                  <input type="text" id="subject" formControlName="subject"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
                    placeholder="Brief summary of the issue" />
                  <p *ngIf="
                      ticketForm.get('subject')?.touched && ticketForm.get('subject')?.errors?.['required']
                    " class="mt-1 text-sm text-red-600">
                    Subject is required
                  </p>
                </div>

                <div>
                  <label for="description" class="block text-sm font-medium text-gray-700">Description *</label>
                  <textarea id="description" formControlName="description" rows="4"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
                    placeholder="Please provide detailed information about your issue"></textarea>
                  <p *ngIf="
                      ticketForm.get('description')?.touched &&
                      ticketForm.get('description')?.errors?.['required']
                    " class="mt-1 text-sm text-red-600">
                    Description is required
                  </p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Applications *</label>
                  <div class="relative">
                    <input type="text"
                      class="w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm"
                      placeholder="Search applications..." (input)="onApplicationSearch($event)" />
                  </div>

                  <div *ngIf="filteredApplications.length > 0"
                    class="mt-2 border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto"
                    (scroll)="onApplicationsScroll($event)">
                    <div class="space-y-2">
                      <div *ngFor="let app of filteredApplications" class="flex items-center">
                        <input [id]="'app-' + app.id" type="checkbox"
                          class="h-4 w-4 text-vodafone-red focus:ring-vodafone-red border-gray-300 rounded"
                          (change)="toggleApplication(app)" [checked]="isApplicationSelected(app)" />
                        <label [for]="'app-' + app.id" class="ml-2 block text-sm text-gray-700">{{ app.label }}</label>
                      </div>

                      <!-- Loading indicator -->
                      <div *ngIf="isLoadingMoreApps" class="flex justify-center py-2">
                        <div class="flex space-x-1">
                          <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                          <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse animation-delay-200"></div>
                          <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse animation-delay-400"></div>
                        </div>
                      </div>

                      <!-- End of results message -->
                      <div *ngIf="!hasMoreApps && filteredApplications.length > 0"
                        class="text-center text-sm text-gray-500 py-1">
                        End of results
                      </div>
                    </div>
                  </div>

                  <!-- No results message -->
                  <div *ngIf="filteredApplications.length === 0 && !isLoadingMoreApps"
                    class="mt-2 text-center text-sm text-gray-500 py-2 border border-gray-300 rounded-md">
                    No applications found
                  </div>

                  <div *ngIf="selectedApplications.length > 0" class="mt-2">
                    <div class="flex flex-wrap gap-2">
                      <div *ngFor="let app of selectedApplications"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-vodafone-red bg-opacity-10 text-vodafone-red">
                        {{ app.label }}
                        <button type="button"
                          class="ml-1 inline-flex items-center p-0.5 rounded-full text-vodafone-red hover:bg-vodafone-red hover:bg-opacity-20"
                          (click)="removeApplication(app)">
                          <span class="sr-only">Remove</span>
                          <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                            fill="currentColor">
                            <path fill-rule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clip-rule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <p *ngIf="
                      ticketForm.get('applications')?.touched && 
                      (ticketForm.get('applications')?.errors?.['required'] || 
                       ticketForm.get('applications')?.errors?.['minLength'])
                    " class="mt-1 text-sm text-red-600">
                    At least one application is required
                  </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label for="impactLevel" class="block text-sm font-medium text-gray-700">Impact Level *</label>
                    <select id="impactLevel" formControlName="impactLevel"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm">
                      <option value="">Select Impact Level</option>
                      <option value="none">None</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    <p *ngIf="
                        ticketForm.get('impactLevel')?.touched && ticketForm.get('impactLevel')?.errors?.['required']
                      " class="mt-1 text-sm text-red-600">
                      Impact level is required
                    </p>
                  </div>

                  <div>
                    <label for="urgency" class="block text-sm font-medium text-gray-700">Urgency *</label>
                    <select id="urgency" formControlName="urgency"
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm">
                      <option value="">Select Urgency</option>
                      <option value="none">None</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    <p *ngIf="
                        ticketForm.get('urgency')?.touched && ticketForm.get('urgency')?.errors?.['required']
                      " class="mt-1 text-sm text-red-600">
                      Urgency is required
                    </p>
                  </div>
                </div>

                <div>
                  <label for="priority" class="block text-sm font-medium text-gray-700">Priority <span
                      class="text-sm text-gray-500">(Fixed)</span></label>
                  <div class="mt-1 relative">
                    <select id="priority" formControlName="priority"
                      class="block w-full rounded-md border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red sm:text-sm bg-gray-100 cursor-not-allowed"
                      [disabled]="true">
                      <option value="P4">P4 - Low</option>
                    </select>
                    <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                        fill="currentColor">
                        <path fill-rule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <p class="mt-1 text-xs text-gray-500 flex items-center">
                    <svg class="h-3 w-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Priority is fixed to P4 - Low
                  </p>
                </div>
              </form>
            </div>

            <!-- Submit button -->
            <div class="border-t border-gray-200 px-4 py-6 sm:px-6">
              <div class="flex justify-end space-x-3">
                <button type="button"
                  class="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vodafone-red"
                  (click)="hideTicketSidebar()">
                  Cancel
                </button>
                <button type="button" [disabled]="ticketForm.invalid"
                  class="inline-flex justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-vodafone-red hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vodafone-red disabled:opacity-50 disabled:cursor-not-allowed"
                  (click)="submitTicket()">
                  Create Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>